import { chain, clone, concat, get, isEmpty, isNil, isNumber, map, omit, orderBy, partition, pick, reduce, set, toLower } from "lodash"
import { createSelector } from "reselect"
import { computeGroupsMap, contributorsReducer, mergeContributorsMetrics, extractContributorRiskFactor, computeAggregatedColumnTotals, getAssetsReviewColumnsByCategories, extractContributorComposition } from "helpers/assets"
import { getIsNoLongerHeldFromContributor, getTickerAndMicFromContributor, isUnmodeled } from "helpers/securities"
import { buildContributorKey, extractContributorRisk } from "helpers/contributors"
import { computeFactorSensitivityMap, computeGroupedFactorSensitivityMap } from "helpers/factorSensitivity"
import { computeGroupedMarginalContributionMap, computeMarginalContributionMap } from "helpers/marginalContribution"
import { makeCustomFormatter, FORMATS } from "helpers/formatters/numbers"
import { getCategories } from "selectors/factors"
import { getSelectedView } from "selectors/assets"
import { Creators as IntradayAssetTableCreators } from "../model"
import { getColumns } from "./columns"
import { getGroupingOptions, getRawSort, getSelectedGroup, getSelectedMetricType, getSelectedPage, getSelectedSubView } from "."

const ITEMS_PER_PAGE = 200
const EMPTY_ARRAY = []

export const getCompositionStatus = state => get(state, "global.data.intradayComposition.status")
const getRawPositions = state => get(state, "global.data.intradayComposition.data.model.intradaySimulation.composition.0.positions", EMPTY_ARRAY)
const getRawGroupedPositions = state => get(state, "global.data.intradayGroupedComposition.data.model.intradaySimulation.composition.0.compositionBy", EMPTY_ARRAY)
const getRawNonActivePositions = state => get(state, "global.data.intradayComposition.data.model.nonActive.composition.0.positions", EMPTY_ARRAY)
const getRawGroupedNonActivePositions = state => get(state, "global.data.intradayGroupedComposition.data.model.nonActive.composition.0.compositionBy", EMPTY_ARRAY)
const getRawExposureContributors = state => get(state, "global.data.intradayExposure.data.model.intradaySimulation.exposureContributors.0.contributors", EMPTY_ARRAY)
const getRawGroupedExposureContributors = state => get(state, "global.data.intradayGroupedExposure.data.model.intradaySimulation.groupedExposureContributors.0.groupedContributors", EMPTY_ARRAY)
const getRawRiskContributors = state => get(state, "global.data.intradayRisk.data.model.intradaySimulation.riskContributors", EMPTY_ARRAY)
const getRawGroupedRiskContributors = state => get(state, "global.data.intradayGroupedRisk.data.model.intradaySimulation.groupedRiskContributors", EMPTY_ARRAY)
const getRawBetaContributors = state => get(state, "global.data.intradayBeta.data.model.intradaySimulation.betaContributors.0.contributors")
const getRawGroupedBetaContributors = state => get(state, "global.data.intradayGroupedBeta.data.model.intradaySimulation.groupedBetaContributors.0.groupedContributors")
const getRawRiskAttribution = state => get(state, "global.data.intradayRisk.data.model.intradaySimulation.risk.0.total")
const getRawRiskFactorsContributors = state => get(state, "global.data.intradayRiskFactors.data.model.intradaySimulation.riskContributors", EMPTY_ARRAY)
const getRawGroupedRiskFactorsContributors = state => get(state, "global.data.intradayGroupedRiskFactors.data.model.intradaySimulation.groupedRiskContributors", EMPTY_ARRAY)
const getRawFactorSensitivityContributors = state => get(state, "global.data.intradayFactorSensitivity.data.model.intradaySimulation.factorSensitivityContributors", EMPTY_ARRAY)
const getRawGroupedFactorSensitivityContributors = state => get(state, "global.data.intradayGroupedFactorSensitivity.data.model.intradaySimulation.groupedFactorSensitivityContributors", EMPTY_ARRAY)
const getRawMarginalContributionContributors = state => get(state, "global.data.intradayMarginalContribution.data.model.intradaySimulation.riskContributors", EMPTY_ARRAY)
const getRawGroupedMarginalContributionContributors = state => get(state, "global.data.intradayGroupedMarginalContribution.data.model.intradaySimulation.groupedRiskContributors", EMPTY_ARRAY)
const getSegment = (_, props) => get(props, "segment")

const numberFormatter = makeCustomFormatter(FORMATS.NUMBER_FORMAT, { emptyValueFiller: "-" })
const sumEconomicExposureReducer = (acc, contributor) => {
  const key = buildContributorKey(contributor)
  if (acc[key]) {
    acc[key] += contributor.economicExposure
  } else {
    acc[key] = clone(contributor.economicExposure)
  }
  return acc
}

const getEconomicExposuresMap = createSelector(
  [getRawNonActivePositions],
  (positions) => chain(positions)
    .reduce(contributorsReducer, {})
    .mapValues(item => item.economicExposure)
    .value(),
)

const getGroupedEconomicExposuresMap = createSelector(
  [getRawGroupedNonActivePositions],
  (positions) =>
    reduce(positions, (acc, { id, economicExposure }) => set(acc, id, economicExposure), {}),
)

const getGroupedEconomicExposuresContributorsMap = createSelector(
  [getRawGroupedNonActivePositions],
  (positions) => chain(positions)
    .keyBy("id")
    .mapValues(group => chain(group)
      .get("contributors")
      .reduce(sumEconomicExposureReducer, {})
      .value())
    .value(),
)

const extractContributorExposure = (contributor) => ({
  exposure: reduce(contributor.factors, (acc, factor) => set(acc, factor.id, get(factor, "exposure")), {}),
})

const extractContributorBeta = (contributor) => ({
  beta: {
    ...pick(contributor, ["predicted", "historical"]),
    standalone: get(contributor, "securityBeta"),
  },
})

const getCompositionMap = createSelector(
  [getRawPositions, getEconomicExposuresMap, getRawGroupedPositions, getGroupedEconomicExposuresMap, getGroupingOptions],
  (positions, expMap, groupedPositions, groupedExpMap, groupingOptions) => {
    const contributors = groupingOptions ? groupedPositions : positions
    const exposureMap = groupingOptions ? groupedExpMap : expMap

    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorComposition(contributor, exposureMap)), {})
  },
)

const getGroupedCompositionContributorsMap = createSelector(
  [getRawGroupedPositions, getGroupedEconomicExposuresContributorsMap],
  (groups, exposureMap) => computeGroupsMap(groups, { mapFn: extractContributorComposition, exposureMap }),
)

const getExposureMap = createSelector(
  [getRawExposureContributors, getRawGroupedExposureContributors, getGroupingOptions],
  (exposureContributors, groupedExposureContributors, groupingOptions) => {
    const contributors = !groupingOptions ? exposureContributors : groupedExposureContributors
    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorExposure(contributor)), {})
  },
)

const getGroupedExposureContributorsMap = createSelector(
  [getRawGroupedExposureContributors],
  (groups) => computeGroupsMap(groups, { mapFn: extractContributorExposure }),
)

const getRiskMap = createSelector(
  [getRawRiskContributors, getRawGroupedRiskContributors, getGroupingOptions],
  (riskContributors, groupedRiskContributors, groupingOptions) => {
    const contributors = !groupingOptions ? riskContributors : groupedRiskContributors
    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorRisk(contributor)), {})
  },
)

const getGroupedRiskContributorsMap = createSelector(
  [getRawGroupedRiskContributors],
  (groups) => computeGroupsMap(groups, { mapFn: extractContributorRisk }),
)

const getBetaMap = createSelector(
  [getRawBetaContributors, getRawGroupedBetaContributors, getGroupingOptions],
  (betaContributors, groupedBetaContributors, groupingOptions) => {
    const contributors = !groupingOptions ? betaContributors : groupedBetaContributors
    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorBeta(contributor)), {})
  },
)

const getGroupedBetaContributorsMap = createSelector(
  [getRawGroupedBetaContributors],
  (groups) => computeGroupsMap(groups, { mapFn: extractContributorBeta }),
)

const getRiskFactorsMap = createSelector(
  [getRawRiskFactorsContributors, getRawGroupedRiskFactorsContributors, getGroupingOptions, getSelectedSubView],
  (riskFactorsContributors, groupedRiskFactorsContributors, groupingOptions, selectedSubView) => {
    const contributors = !groupingOptions ? riskFactorsContributors : groupedRiskFactorsContributors
    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorRiskFactor(contributor, selectedSubView)), {})
  },
)

const getGroupedRiskFactorsContributorsMap = createSelector(
  [getRawGroupedRiskFactorsContributors],
  (groups) => computeGroupsMap(groups, { mapFn: extractContributorRiskFactor }),
)

const getFactorSensitivityMap = createSelector(
  [getRawFactorSensitivityContributors, getRawGroupedFactorSensitivityContributors, getGroupingOptions],
  computeFactorSensitivityMap,
)

const getGroupedFactorSensitivityMap = createSelector(
  [getRawGroupedFactorSensitivityContributors],
  computeGroupedFactorSensitivityMap,
)

const getMarginalContributionMap = createSelector(
  [getRawMarginalContributionContributors, getRawGroupedMarginalContributionContributors, getGroupingOptions],
  computeMarginalContributionMap,
)

const getGroupedMarginalContributionMap = createSelector(
  [getRawGroupedMarginalContributionContributors],
  computeGroupedMarginalContributionMap,
)

export const getDataMap = createSelector(
  [getCompositionMap, getExposureMap, getRiskMap, getRiskFactorsMap, getBetaMap, getFactorSensitivityMap, getMarginalContributionMap],
  mergeContributorsMetrics,
)

const getGroupContributorsDataMap = createSelector(
  [getGroupedCompositionContributorsMap, getGroupedExposureContributorsMap, getGroupedRiskContributorsMap, getGroupedRiskFactorsContributorsMap, getGroupedBetaContributorsMap, getGroupedFactorSensitivityMap, getGroupedMarginalContributionMap],
  mergeContributorsMetrics,
)

const computeGroupAggregatedColumns = (label, group, columns) =>
  reduce(columns, (acc, { path }) =>
    set(acc, path, get(group, path)), {
    id: label,
    isAggregate: true,
  })

const getContributorId = (contributor) => !isEmpty(contributor.id) ? contributor.id : "Unknown"

const computeContributorData = (contributor) => ({
  id: getContributorId(contributor),
  ...getTickerAndMicFromContributor(contributor),
  ...omit(contributor, ["id"]),
  isUnmodeled: isUnmodeled(contributor),
  isNoLongerHeld: getIsNoLongerHeldFromContributor(contributor),
  isNotHeld: get(contributor, "notHeld"),
})

const computeGroupData = (item) => ({
  ...item,
  label: item.description,
  isGroup: true,
})

const getCurrentData = createSelector(
  [getDataMap, getGroupContributorsDataMap, getSelectedGroup, getGroupingOptions],
  (data, groupContributorsData, selectedGroup, groupingOptions) => {
    if (groupingOptions && !selectedGroup) {
      return map(data, computeGroupData)
    }
    if (selectedGroup) {
      data = groupContributorsData[selectedGroup]
    }

    const contributors = chain(data)
      .filter(contributor => !contributor.notHeld)
      .map(computeContributorData)
      .value()

    return contributors
  },
)

export const getOrderedSecurities = createSelector(
  [getCurrentData, getRawSort, getColumns],
  (data, sort, columns) => {
    const direction = get(sort, "direction")
    const absSort = get(sort, "absolute", false)

    let sortedColumnPath = sort.id
    if (sortedColumnPath !== "id") {
      sortedColumnPath = chain(columns)
        .find(column => column.id === sort.id)
        .get("path")
        .value()
    }

    let [positions, missingValuePositions] = partition(data, item => {
      const value = get(item, sortedColumnPath)
      return !!value || value === 0
    })
    const order = [pos => {
      const value = get(pos, sortedColumnPath)
      return absSort && isNumber(value) ? Math.abs(value) : value
    }]
    const dir = [toLower(direction)]

    // Resolve ties
    if (sort.id !== "id") {
      order.push("id")
      dir.push("asc")
    }
    positions = orderBy(positions, order, dir)
    missingValuePositions = orderBy(missingValuePositions, "id", "asc")

    return concat(positions, missingValuePositions)
  },
)

export const getSecurities = createSelector(
  [getOrderedSecurities, getSelectedPage],
  (orderedSecurities, selectedPage) => {
    const start = selectedPage * ITEMS_PER_PAGE
    return orderedSecurities.slice(start, start + ITEMS_PER_PAGE)
  },
)

export const getSelectedGroupName = createSelector(
  [getSelectedGroup, getCompositionMap],
  (selectedGroup, groups) =>
    chain(groups)
      .find(group => group.id === selectedGroup)
      // A group might be missing if zero exposure and non-zero pnl. Use fallback
      .get("description", selectedGroup)
      .value(),
)

export const getAggregatedData = createSelector(
  [getOrderedSecurities, getRawRiskAttribution, getColumns, getSelectedGroup, getSelectedGroupName, getSegment, getDataMap, getSelectedSubView, getSelectedMetricType],
  (securities, riskAttribution, columns, selectedGroup, selectedGroupName, segment, dataMap, selectedSubView, metricType) => {
    const isDrillDown = !!selectedGroup
    let aggregatedData
    if (isDrillDown) {
      aggregatedData = computeGroupAggregatedColumns(selectedGroupName, dataMap[selectedGroup], columns, selectedSubView)
    } else {
      aggregatedData = computeAggregatedColumnTotals(segment ? "SEGMENT" : "TOTAL", securities, columns, metricType)
    }
    const totalRisk = get(aggregatedData, "risk.total")
    if (!isDrillDown && !isNil(totalRisk)) {
      aggregatedData.risk.total = {
        value: totalRisk,
        tooltipContents: [`Total Risk: ${numberFormatter(riskAttribution * 100)}%`],
      }
    }

    return {
      ...aggregatedData,
      isDrillDown,
      action: IntradayAssetTableCreators.setSelectedGroup,
    }
  },
)

export const getColumnsByCategories = createSelector(
  [getColumns, getCurrentData, getSelectedSubView, getSelectedView, getSelectedMetricType, getCategories],
  getAssetsReviewColumnsByCategories,
)

export const getQueries = createSelector(
  [getColumns],
  (columns) => reduce(columns, (acc, column) => {
    acc[column.query] = true
    return acc
  }, {}),
)
